import React, {MouseEvent, useCallback, useEffect, useRef, useState} from "react"
import Modal from "./Modal";
import {ChromePicker, ColorResult} from "react-color";
import Title, {Props as TitleProps} from "./Title";
import Button, {Props as ButtonProps} from "./Button";
import styles from '../css/ColorPicker.module.css'
import classNames from "classnames";

export interface Props {
    id: string,
    show?: boolean,
    titleProps?: TitleProps,
    buttonProps?: ButtonProps,
    onDismiss?: (event: MouseEvent) => void,
    defaultColor: string,
    onChange?: (v: string) => void
    onChangeComplete?: (v: string) => void
    onValidate?: (v: string) => void
    onUserStartInteracting?: (v: boolean) => void // fire true when user start changing the color and fire false at the end
}

export default (props: Props) => {
    const {id, defaultColor, show, onDismiss, onUserStartInteracting, onChange, titleProps, onChangeComplete, onValidate, buttonProps} = props;

    const wrapperRef = useRef(null);
    const [pickedColor, setPickedColor] = useState(defaultColor || '#ff0000');
    const [isChanging, setIsChanging] = useState(false);
    const [isMouseDown, setIsMouseDown] = useState(false);

    const handleOnIsChangingChange = useCallback((value: boolean) => {
        if (!show) return;
        setIsChanging(value);
        onUserStartInteracting && onUserStartInteracting(value)
    }, [show, onUserStartInteracting]);

    // eslint-disable-next-line
    const handleOnChange = useCallback(
        ({rgb: {r, g, b, a}}: ColorResult) => {
            if (!isChanging && isMouseDown) handleOnIsChangingChange(true);

            onChange && onChange(`rgba(${r}, ${g}, ${b}, ${a})`);
            setPickedColor(`rgba(${r}, ${g}, ${b}, ${a})`);
        },
        [isChanging, isMouseDown, onChange, handleOnIsChangingChange, setPickedColor],
    );

    const handleOnChangeCompleted = useCallback(
        () => {
            handleOnIsChangingChange.bind(null, false);
            onChangeComplete && onChangeComplete(pickedColor)
        },
        [pickedColor, handleOnIsChangingChange, onChangeComplete],
    );

    const handleOnValidate = useCallback(
        () => {
            onValidate && onValidate(pickedColor)
        },
        [pickedColor, onValidate],
    );

    useEffect(() => {
        show && setPickedColor(defaultColor || '#ff0000')
    }, [show, defaultColor]);

    useEffect(() => {
        const handleClickUp = () => {
            setIsMouseDown(false);
            handleOnIsChangingChange(false);
        };
        const handleClickDown = setIsMouseDown.bind(null, true);
        document.addEventListener("mousedown", handleClickDown);
        document.addEventListener("touchstart", handleClickDown);
        document.addEventListener("mouseup", handleClickUp);
        document.addEventListener("touchend", handleClickUp);
        return () => {
            document.removeEventListener("mousedown", handleClickDown);
            document.removeEventListener("touchstart", handleClickDown);
            document.removeEventListener("mouseup", handleClickUp);
            document.removeEventListener("touchend", handleClickUp);
        };
    }, [wrapperRef, handleOnIsChangingChange]);


    return (
        <Modal hideCross={isChanging} backdropTransparent={isChanging} disabledBackdropDismiss id={id} show={show}
               onDismiss={onDismiss}
               center>

            {
                titleProps &&
                <div className={classNames({[styles.pickerTitle]: true, [styles.notVisible]: isChanging})}>
                    <Title {...titleProps} />
                </div>
            }

            <div ref={wrapperRef} className={classNames({
                [styles.seeThrough]: isChanging
            }, styles.picker)}>
                <ChromePicker
                    color={pickedColor}
                    onChange={handleOnChange}
                    onChangeComplete={handleOnChangeCompleted}
                />
            </div>

            {
                buttonProps &&
                <Button className={classNames({
                    [styles.notVisible]: isChanging
                })} onClick={handleOnValidate} {...buttonProps}/>
            }
        </Modal>
    )
}
